const spaceGenericThreeFourths = '0.75rem';
const appInfoFocusColor = 'rgba(255, 255, 255, 0.25)';

const defendFromHostileStyles = {
  margin: 'unset',
  padding: 'unset',
  lineHeight: 'auto',
  marginTop: 'unset',
  marginBottom: 'unset',
  boxSizing: 'initial',
  '& div': { margin: 'unset' },
  '& svg, button': {
    lineHeight: 'auto',
    marginTop: 'auto',
    marginBottom: 'auto',
    boxSizing: 'default',
    margin: 'unset'
  }
};

function carinaFontReset(theme) {
  return {
    ...defendFromHostileStyles,
    fontFamily: theme.font.base.family,
    fontStyle: 'normal',
    fontSize: theme.font.base.size.s,
    fontWeight: theme.font.base.weight.regular,
    lineHeight: theme.font.lineHeight.base,
    letterSpacing: 'normal',
    textIndent: 0,
    textShadow: 'none',
    textTransform: 'none'
  };
}

export function getAlertStyles({
  appearance,
  theme,
  isRtl,
  opacity,
  variant,
  maxWidth
}) {
  const boxShadow = theme.depth.boxShadow.hard;
  return {
    ...carinaFontReset(theme),
    backgroundColor:
      variant === 'app'
        ? theme.backgroundColor[
            appearance === 'info' ? 'interactive' : appearance
          ].fill.base
        : theme.backgroundColor.base,
    borderColor:
      theme.borderColor[appearance === 'info' ? 'interactive' : appearance]
        .base,
    borderStyle: 'solid',
    borderWidth: theme.size.border.base,
    boxSizing: 'border-box',
    boxShadow:
      variant === 'user'
        ? `${boxShadow.xOffset} ${boxShadow.yOffset} ${boxShadow.blurRadius}
            ${boxShadow.spreadRadius} ${boxShadow.color}`
        : 'none',
    color:
      variant === 'app'
        ? theme.font.color[appearance === 'info' ? 'interactive' : appearance]
            .fill
        : theme.font.color.base,
    direction: isRtl ? 'rtl' : 'ltr',
    display: 'flex',
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    maxWidth,
    minHeight: '2.5rem',
    opacity,
    overflow: 'auto',
    position: 'relative',
    textAlign: isRtl ? 'right' : 'left',
    transform: 'translate3d(0, 0, 0)',
    transition: `opacity ${theme.transition.duration.default} linear`,
    width: '100%',
    '&:not(:only-child)': {
      marginBottom: variant === 'page' ? '1rem' : 0
    },
    a: {
      ...carinaFontReset(theme),
      color:
        variant === 'app'
          ? theme.font.color[appearance === 'info' ? 'interactive' : appearance]
              .fill
          : theme.font.color.base,
      display: 'inline-block',
      textDecoration: 'underline',
      '&:hover': {
        color:
          variant === 'app'
            ? theme.font.color[
                appearance === 'info' ? 'interactive' : appearance
              ].fill
            : theme.font.color.base,
        textDecoration: 'underline'
      },
      '&:focus': {
        outlineColor:
          variant === 'app' && appearance === 'info'
            ? appInfoFocusColor
            : theme.backgroundColor.interactive.fill.focus,
        outlineStyle: 'solid',
        outlineWidth: theme.size.border.focus,
        outlineOffset: '0.25rem'
      },
      '&:active': {
        color:
          variant === 'app'
            ? theme.font.color[
                appearance === 'info' ? 'interactive' : appearance
              ].fill
            : theme.font.color.base,
        outline: 'none'
      }
    }
  };
}

export function getBodyColStyles({ variant }) {
  return {
    ...defendFromHostileStyles,
    display: 'flex',
    flexGrow: 1,
    justifyContent: variant === 'app' ? 'center' : 'unset'
  };
}

export function getContainerStyles({ variant }) {
  return {
    ...defendFromHostileStyles,
    display: variant === 'app' ? 'inline-flex' : 'flex',
    MsFlex: '0 0 auto',
    flexGrow: variant === 'app' ? 'unset' : 1
  };
}

export function getIconColStyles({ appearance, theme, isRtl, variant }) {
  let rightPadding = variant === 'app' ? 0 : spaceGenericThreeFourths;
  let leftPadding = spaceGenericThreeFourths;
  return {
    ...defendFromHostileStyles,
    flexShrink: 0,
    alignItems: variant === 'app' ? 'flex-start' : 'center',
    display: 'flex',
    backgroundColor:
      theme.backgroundColor[appearance === 'info' ? 'interactive' : appearance]
        .fill.base,
    lineHeight: 0,
    paddingTop: spaceGenericThreeFourths,
    paddingRight: isRtl ? leftPadding : rightPadding,
    paddingBottom: spaceGenericThreeFourths,
    paddingLeft: isRtl ? rightPadding : leftPadding,
    svg: {
      fill:
        appearance === 'info'
          ? theme.backgroundColor.interactive.base
          : theme.backgroundColor[appearance].base
    }
  };
}

export function getContentColStyles({ isRtl, variant }) {
  let rightPadding = 0;
  let leftPadding = variant === 'app' ? '0.5rem' : spaceGenericThreeFourths;
  return {
    ...defendFromHostileStyles,
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    flexGrow: 1,
    margin: '0 auto',
    maxWidth: '100%',
    MsFlex: '1 0 0',
    paddingRight: isRtl ? leftPadding : rightPadding,
    paddingLeft: isRtl ? rightPadding : leftPadding,
    paddingTop: '0.25rem',
    paddingBottom: '0.25rem',
    textAlign: variant === 'app' ? 'center' : 'left'
  };
}

export function getMessageColStyles({
  isRtl,
  dismissible,
  buttonText,
  buttonStacked,
  variant
}) {
  let rightPadding = spaceGenericThreeFourths;
  let leftPadding = 0;
  if (buttonText) {
    if (variant === 'app') {
      rightPadding = '1rem';
    } else {
      rightPadding = '1.5rem';
    }
  } else if (dismissible) {
    rightPadding = '1rem';
  }
  return {
    ...defendFromHostileStyles,
    alignSelf: 'center',
    paddingTop: '0',
    maxWidth:
      variant === 'app'
        ? 'unset'
        : `calc(100% - ${isRtl ? leftPadding : rightPadding})`,
    width: buttonStacked ? '100%' : 'unset',
    paddingLeft: isRtl ? rightPadding : leftPadding
  };
}

export function getHeadingStyles({ appearance, theme, text, variant }) {
  const convertedAppearance =
    appearance === 'info' ? 'interactive' : appearance;
  return {
    ...carinaFontReset(theme),
    color:
      variant === 'app'
        ? theme.font.color[convertedAppearance].fill
        : theme.font.color.base,
    fontWeight: theme.font.base.weight.bold,
    marginBottom: text ? '0.5rem' : 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    a: {
      fontWeight: theme.font.base.weight.bold
    }
  };
}

export function getDescriptionStyles({ appearance, theme, variant }) {
  return {
    ...carinaFontReset(theme),
    color:
      variant === 'app'
        ? theme.font.color[appearance === 'info' ? 'interactive' : appearance]
            .fill
        : theme.font.color.base,
    marginBottom: 0,
    marginLeft: 8,
    marginRight: 8,
    marginTop: 0
  };
}

export function getListStyles({ appearance, theme, isRtl, variant }) {
  const convertedAppearance =
    appearance === 'info' ? 'interactive' : appearance;
  return {
    ...carinaFontReset(theme),
    color:
      variant === 'app'
        ? theme.font.color[convertedAppearance].fill
        : theme.font.color.base,
    display: 'inline-block',
    lineHeight: theme.font.lineHeight.list,
    listStyle: 'disc',
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    paddingBottom: 0,
    paddingLeft: isRtl ? 0 : '1.5rem',
    paddingRight: isRtl ? '1.5rem' : 0,
    paddingTop: 0
  };
}

export function getListItemStyles({ appearance, theme, variant }) {
  const convertedAppearance =
    appearance === 'info' ? 'interactive' : appearance;
  return {
    ...carinaFontReset(theme),
    color:
      variant === 'app'
        ? theme.font.color[convertedAppearance].fill
        : theme.font.color.base,
    lineHeight: theme.font.lineHeight.list,
    marginBottom: '0.25rem',
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    '&:last-child': {
      marginBottom: 0
    }
  };
}

export function getButtonColStyles({ appearance, theme, isRtl, variant }) {
  let rightPadding = spaceGenericThreeFourths;
  let leftPadding = 0;
  return {
    ...defendFromHostileStyles,
    paddingTop: variant === 'app' ? 3 : 4,
    paddingBottom: variant === 'app' ? 3 : 4,
    paddingRight: isRtl ? leftPadding : rightPadding,
    paddingLeft: isRtl ? rightPadding : leftPadding,
    ...addAppButtonColStyles(appearance, theme, variant)
  };
}

function addAppButtonColStyles(appearance, theme, variant) {
  const convertedAppearance =
    appearance === 'info' ? 'interactive' : appearance;
  if (variant === 'app') {
    return {
      ...defendFromHostileStyles,
      flex: '1 1 auto',
      paddingLeft: 16,
      paddingRight: 16,
      display: 'inline-flex',
      justifyContent: 'center',
      button: {
        marginTop: 1,
        borderColor: theme.font.color[convertedAppearance].fill,
        color: theme.font.color[convertedAppearance].fill,
        display: 'inline-flex',
        '&:hover': {
          borderColor: theme.font.color[convertedAppearance].fill,
          color: theme.font.color[convertedAppearance].fill
        },
        ...(appearance === 'info' && {
          '&:focus': {
            boxShadow: `0 0 0 ${theme.size.border.focus}px ${appInfoFocusColor}`
          }
        })
      }
    };
  }
}

export function getDismissColStyles({ isRtl }) {
  return {
    ...defendFromHostileStyles,
    paddingTop: spaceGenericThreeFourths,
    paddingRight: isRtl ? 0 : spaceGenericThreeFourths,
    paddingBottom: spaceGenericThreeFourths,
    paddingLeft: isRtl ? spaceGenericThreeFourths : 0,
    // Fixes misaligned dismiss button in IE
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      paddingRight: isRtl ? 0 : 32,
      paddingLeft: isRtl ? 32 : 0
    },
    height: 24,
    width: 24
  };
}

export function getDismissButtonStyles({ appearance, theme, variant }) {
  let backgroundColor =
    theme.backgroundColor[appearance === 'info' ? 'interactive' : appearance]
      .fill;

  return {
    ...defendFromHostileStyles,
    appearance: 'none',
    background: 'none',
    border: 'none',
    borderRadius: theme.size.border.radius.twoX,
    boxSizing: 'border-box',
    cursor: 'pointer',
    fontSize: theme.font.base.size.m,
    lineHeight: 0,
    margin: 0,
    outline: 0,
    padding: 0,
    textAlign: 'center',
    '&:hover': {
      backgroundColor: backgroundColor.hover,
      svg: {
        fill:
          theme.font.color[appearance === 'info' ? 'interactive' : appearance]
            .fill
      }
    },
    '&:focus': {
      boxShadow: `0 0 0 ${theme.size.border.focus}px ${backgroundColor.focus}`
    },
    ...(variant === 'app' &&
      appearance === 'info' && {
        '&:focus': {
          boxShadow: `0 0 0 ${theme.size.border.focus}px ${appInfoFocusColor}`
        }
      }),
    '&:active': {
      backgroundColor: backgroundColor.active,
      boxShadow: 'none',
      svg: {
        fill:
          theme.font.color[appearance === 'info' ? 'interactive' : appearance]
            .fill
      }
    },
    svg: {
      fill:
        variant === 'app'
          ? theme.font.color[appearance === 'info' ? 'interactive' : appearance]
              .fill
          : theme.font.color.base
    }
  };
}
